import FeatherIcon from "feather-icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import classnames from "classnames";
import {
  Card,
  CardHeader,
  CardBody,
  Modal,
  Col,
  Row,
  Spinner,
  Input,
  Button,
  Nav,
  NavItem,
  NavLink,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import api from "../helpers/api";
import { formatCreatedDate, urgencyColors } from "../constants/utils";
import PermissionGuard from "../components/Common/PermissionGuard";
import usePermission from "../Hooks/usePermission";
import ProcurementRequestModal from "../components/ProcurementRequestModal";
import RejectProcurementRequestModal from "../components/RejectProcurementRequestModal";
import TransferResolutionModal from "../components/TransferResolutionModal";
import AssetTransferModal from "../components/AssetTransferModal";
import PurchaseOrders from "../components/PurchaseOrders";
import ProcurementAnalytics from "../components/ProcurementAnalytics";
import TransfersTab from "../components/planning/TransfersTab";
import {
  PROCUREMENT_REQUEST_CREATE,
  PROCUREMENT_REQUEST_RESOLVE,
  PURCHASE_ORDER_READ,
  PROCUREMENT_ANALYTICS,
  ASSET_TRANSFER,
} from "../constants/permissions";

const STATUS_TABS = ["SUBMITTED", "APPROVED_PO", "APPROVED_TRANSFER", "REJECTED", "CLOSED"];

const statusMeta = {
  SUBMITTED: { icon: "inbox", colorClass: "bg-warning text-white", border: "#ffc107" },
  APPROVED_PO: { icon: "check-circle", colorClass: "bg-success text-white", border: "#28a745" },
  APPROVED_TRANSFER: { icon: "repeat", colorClass: "bg-primary text-white", border: "#0d6efd" },
  REJECTED: { icon: "x-circle", colorClass: "bg-danger text-white", border: "#dc3545" },
  CLOSED: { icon: "check-square", colorClass: "bg-secondary text-white", border: "#6c757d" },
};

const PlanningPurchasing = () => {
  const { t } = useTranslation();
  document.title = t("shared.planning", "Planning") + " | Arcmedix";

  const currentUser = JSON.parse(localStorage.getItem("authUser"));
  const organizationId = currentUser?.activeOrganizationId ?? null;
  const orgCurrency = currentUser?.activeOrganizationCurrency || "USD";
  const { hasPermission } = usePermission();
  const canResolve = hasPermission(PROCUREMENT_REQUEST_RESOLVE);
  const canViewPO = hasPermission(PURCHASE_ORDER_READ);
  const canTransfer = hasPermission(ASSET_TRANSFER);
  const canViewAnalytics = hasPermission(PROCUREMENT_ANALYTICS);
  const [moduleTab, setModuleTab] = useState("requests");

  const [poPrefill, setPoPrefill] = useState(null);
  const [transferModal, setTransferModal] = useState(false);
  const routerLocation = useLocation();
  // A transfer notification deep-links to /planning?tab=transfers&request=<id>. Driven by an
  // effect on the query string rather than a useState initializer, because "View Transfer" is a
  // client-side navigate(): when the user is already on this page the component does not remount,
  // so an initializer would leave them staring at the Requests tab while the URL says otherwise.
  useEffect(() => {
    const requestedTab = new URLSearchParams(routerLocation.search).get("tab");
    if (requestedTab === "transfers" && canTransfer) {
      setModuleTab("transfers");
    }
  }, [routerLocation.search, canTransfer]);
  const navigate = useNavigate();
  const [transferError, setTransferError] = useState("");
  // Admin-initiated transfer (spec §5): independent of the request queue.
  const [adminTransferModal, setAdminTransferModal] = useState(false);

  // Resolve a request via internal transfer (delegates to the live asset-transfer flow).
  const handleResolveTransfer = async (payload) => {
    setIsSubmitting(true);
    setTransferError("");
    try {
      await api.post("procurement-request/resolve-transfer", payload);
      setTransferModal(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (e) {
      setTransferError(e?.response?.data?.message || t("shared.something_went_wrong", "An error occurred"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build a PO prefill from a request and switch to the Purchase Orders tab.
  const issuePoFromRequest = (req) => {
    setPoPrefill({
      procurementRequestId: req.id,
      requestNumber: req.requestNumber,
      requestTitle: req.title,
      vendorId: req.suggestedVendorId || null,
      vendorName: req.suggestedVendor || "",
      currency: req.currency || orgCurrency || "",
      deliveryAddress: req.locationName || "",
      notes: req.notes || "",
      lines: [{
        // The line type follows the request's category so receiving keeps its behavior
        // (stock update for parts, asset registration for assets) by default.
        itemType: req.requestType === "PARTS_CONSUMABLES" ? "SPARE_PART" : "ASSET",
        description: req.title,
        quantity: req.quantity || 1, unit: req.unit || "",
        unitPrice: req.estimatedPrice || 0, taxRate: orgDefaultTaxRate,
      }],
    });
    setDetailsModal(false);
    setModuleTab("purchase_orders");
  };

  // Role-aware, concise status label
  const statusLabel = useCallback((status) => {
    const admin = {
      SUBMITTED: t("planning_purchasing.status.submitted", "Submitted"),
      APPROVED_PO: t("planning_purchasing.status.approved", "Approved"),
      APPROVED_TRANSFER: t("planning_purchasing.status.transferred", "Transferred"),
      REJECTED: t("planning_purchasing.status.rejected", "Rejected"),
      CLOSED: t("planning_purchasing.status.closed", "Closed"),
    };
    const requester = {
      ...admin,
      REJECTED: t("planning_purchasing.status.declined", "Declined"),
      CLOSED: t("planning_purchasing.status.completed", "Completed"),
    };
    return (canResolve ? admin : requester)[status] || status;
  }, [t, canResolve]);

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}/${d.getFullYear()}`;
  };

  // State
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [detailsModal, setDetailsModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [bulkRejectModal, setBulkRejectModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("SUBMITTED");

  // Bulk selection (admin)
  const [selectedIds, setSelectedIds] = useState([]);

  // Admin notes draft (detail modal)
  const [adminNotesDraft, setAdminNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  // Dropdown data
  const [locations, setLocations] = useState([]);
  const [enumData, setEnumData] = useState([]);
  // Org-level PO defaults (Settings → Organization): tax rate prefilled on new PO lines.
  const [orgDefaultTaxRate, setOrgDefaultTaxRate] = useState(0);

  const isOwner = (request) => {
    const rid = request?.requester?.id ?? request?.requester;
    return rid === currentUser?.id;
  };

  const toggleTab = (tab) => {
    if (statusFilter !== tab) {
      setStatusFilter(tab);
      setRequests([]);
      setCount(10);
      setHasMore(true);
      setSelectedIds([]);
    }
  };

  // Fetch dropdowns
  const fetchEnums = useCallback(async () => {
    try {
      const { data } = await api.get("dynamic-enum/get-all");
      setEnumData(data.response || []);
    } catch (error) {
      console.error("Error fetching enums:", error);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      const { data } = await api.get(
        `organization-location/get-by-organization-id?organizationId=${organizationId}&count=9999&hierarchy=false`
      );
      setLocations(data?.response?.[0]?.locations || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  }, [organizationId]);

  const fetchOrgDefaults = useCallback(async () => {
    if (!organizationId) return;
    try {
      const { data } = await api.get("organization/get-by-id", { params: { id: organizationId } });
      setOrgDefaultTaxRate(Number(data?.response?.defaultTaxRate) || 0);
    } catch (error) {
      console.error("Error fetching organization defaults:", error);
    }
  }, [organizationId]);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (statusFilter) params.append("status", statusFilter);
      params.append("count", count);

      const endpoint = canResolve
        ? `procurement-request/all?${params.toString()}`
        : `procurement-request/my-requests?${params.toString()}`;

      const { data } = await api.get(endpoint);
      setRequests(data?.response || []);
      setHasMore((data?.response?.length || 0) >= count);
    } catch (error) {
      console.error("Error fetching procurement requests:", error);
    } finally {
      setLoading(false);
    }
  }, [count, searchTerm, statusFilter, canResolve]);

  const loadMore = () => {
    if (loading || !hasMore) return;
    setCount((prev) => prev + 10);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCount(10);
    setHasMore(true);
  };

  const toggleCreateModal = (request = null) => {
    setCreateModal(!createModal);
    setSelectedRequest(request || {});
    setErrors({});
    setApiError("");
  };

  // Open with the list row for an instant render, then swap in the full detail
  // (resolution info — transferred assets / linked PO — is only on single reads).
  const toggleDetailsModal = (request = null) => {
    setDetailsModal(!detailsModal);
    setSelectedRequest(request);
    setAdminNotesDraft(request?.adminNotes || "");
    if (request?.id) refreshDetail(request.id);
  };

  const refreshDetail = async (id) => {
    try {
      const { data } = await api.get(`procurement-request/get?id=${id}`);
      if (data?.response) {
        setSelectedRequest(data.response);
        setAdminNotesDraft(data.response.adminNotes || "");
      }
    } catch (error) {
      console.error("Error fetching request detail:", error);
    }
  };

  // Create / update
  const handleSubmit = async (formData) => {
    setIsSubmitting(true);
    setApiError("");
    setErrors({});

    const newErrors = {};
    if (!formData?.requestType) newErrors.requestType = t("planning_purchasing.request_type_required", "Request type is required");
    if (!formData?.title?.trim()) newErrors.title = t("shared.title_required", "Title is required");
    if (!formData?.locationId) newErrors.locationId = t("planning_purchasing.location_required", "Target location is required");
    if (!formData?.quantity || Number(formData.quantity) < 1) newErrors.quantity = t("planning_purchasing.quantity_required", "Quantity must be at least 1");
    if (formData?.usageType === "TEMPORARY") {
      if (!formData.startDate) newErrors.startDate = t("planning_purchasing.start_required", "Start date is required");
      if (!formData.endDate) newErrors.endDate = t("planning_purchasing.end_required", "End date is required");
      if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
        newErrors.endDate = t("planning_purchasing.end_before_start", "End date must be on or after the start date");
      }
    } else if (!formData?.requiredByDate) {
      newErrors.requiredByDate = t("planning_purchasing.required_by_required", "Required-by date is required");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        requestType: formData.requestType,
        title: formData.title,
        quantity: Number(formData.quantity) || 1,
        unit: formData.unit || null,
        urgencyLevel: formData.urgencyLevel || null,
        locationId: formData.locationId,
        assetId: formData.assetId || null,
        usageType: formData.usageType,
        requiredByDate: formData.usageType === "PERMANENT" ? formData.requiredByDate || null : null,
        startDate: formData.usageType === "TEMPORARY" ? formData.startDate || null : null,
        endDate: formData.usageType === "TEMPORARY" ? formData.endDate || null : null,
        notes: formData.notes || null,
        newItemFlag: !!formData.newItemFlag,
        manufacturer: formData.manufacturer || null,
        modelReference: formData.modelReference || null,
        suggestedVendor: formData.suggestedVendor || null,
        suggestedVendorId: formData.suggestedVendorId || null,
        estimatedPrice: formData.estimatedPrice === "" ? null : formData.estimatedPrice,
        estimatedPriceIncludesTax: !!formData.estimatedPriceIncludesTax,
        currency: formData.currency || null,
        attachments: formData.attachments || [],
      };

      if (formData.id) {
        payload.id = formData.id;
        await api.put("procurement-request/update", payload);
      } else {
        await api.post("procurement-request/create", payload);
      }

      setCreateModal(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      console.error("Error submitting procurement request:", error);
      setApiError(error?.response?.data?.message || t("shared.something_went_wrong", "An error occurred"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedRequest?.id) {
        await api.delete(`procurement-request/delete?id=${selectedRequest.id}`);
        setDeleteModal(false);
        setSelectedRequest(null);
        fetchRequests();
      }
    } catch (error) {
      console.error("Error deleting procurement request:", error);
    }
  };

  const handleReject = async (reason) => {
    setIsSubmitting(true);
    try {
      await api.post("procurement-request/reject", {
        requestId: selectedRequest?.id,
        reason,
      });
      setRejectModal(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      console.error("Error rejecting request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBulkReject = async (reason) => {
    setIsSubmitting(true);
    try {
      await api.post("procurement-request/bulk-reject", {
        requestIds: selectedIds,
        reason,
      });
      setBulkRejectModal(false);
      setSelectedIds([]);
      fetchRequests();
    } catch (error) {
      console.error("Error bulk-rejecting requests:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAdminNotes = async () => {
    if (!selectedRequest?.id) return;
    setSavingNotes(true);
    try {
      const { data } = await api.put("procurement-request/admin-notes", {
        requestId: selectedRequest.id,
        adminNotes: adminNotesDraft,
      });
      setSelectedRequest(data?.response || { ...selectedRequest, adminNotes: adminNotesDraft });
      fetchRequests();
    } catch (error) {
      console.error("Error saving admin notes:", error);
    } finally {
      setSavingNotes(false);
    }
  };

  // Bulk selection helpers
  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAll = () => {
    if (selectedIds.length === requests.length) setSelectedIds([]);
    else setSelectedIds(requests.map((r) => r.id));
  };

  // Effects
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Open the create form prefilled when navigated from an asset detail page.
  useEffect(() => {
    const prefill = routerLocation.state?.requestPrefill;
    if (prefill) {
      setSelectedRequest(prefill);
      setErrors({});
      setApiError("");
      setCreateModal(true);
      navigate(routerLocation.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLocation.state]);

  useEffect(() => {
    fetchEnums();
    fetchLocations();
    fetchOrgDefaults();
  }, [fetchEnums, fetchLocations, fetchOrgDefaults]);

  const getStatusBadge = (status) => {
    const meta = statusMeta[status] || { icon: "help-circle", colorClass: "bg-secondary text-white", border: "#6c757d" };
    return (
      <span
        className={`badge rounded-pill py-1 px-2 d-inline-flex align-items-center gap-1 text-uppercase ${meta.colorClass}`}
        style={{ border: `1px solid ${meta.border}` }}
      >
        <FeatherIcon icon={meta.icon} size={12} />
        {statusLabel(status)}
      </span>
    );
  };

  const showSelectColumn = canResolve && statusFilter === "SUBMITTED";
  const isPendingTab = statusFilter === "SUBMITTED";

  return (
    <div className="page-content mt-4">
      {(canViewPO || canViewAnalytics || canTransfer) && (
        /* Module tabs — same Nav-tabs pattern as Reporting and Settings */
        <Nav tabs className="planning-tabs mb-4 border-bottom">
          <NavItem className="flex-shrink-0">
            <NavLink
              className={classnames({ active: moduleTab === "requests" })}
              onClick={() => setModuleTab("requests")}
              style={{ cursor: "pointer" }}
            >
              <FeatherIcon icon="inbox" size={16} className="me-2" />
              {t("planning_purchasing.tab_requests", "Requests")}
            </NavLink>
          </NavItem>
          {canViewPO && (
            <NavItem className="flex-shrink-0">
              <NavLink
                className={classnames({ active: moduleTab === "purchase_orders" })}
                onClick={() => setModuleTab("purchase_orders")}
                style={{ cursor: "pointer" }}
              >
                <FeatherIcon icon="file-text" size={16} className="me-2" />
                {t("planning_purchasing.tab_purchase_orders", "Purchase Orders")}
              </NavLink>
            </NavItem>
          )}
          {canTransfer && (
            <NavItem className="flex-shrink-0">
              <NavLink
                className={classnames({ active: moduleTab === "transfers" })}
                onClick={() => setModuleTab("transfers")}
                style={{ cursor: "pointer" }}
              >
                <FeatherIcon icon="repeat" size={16} className="me-2" />
                {t("business.planning.transfers", "Transfers")}
              </NavLink>
            </NavItem>
          )}
          {canViewAnalytics && (
            <NavItem className="flex-shrink-0">
              <NavLink
                className={classnames({ active: moduleTab === "analytics" })}
                onClick={() => setModuleTab("analytics")}
                style={{ cursor: "pointer" }}
              >
                <FeatherIcon icon="trending-up" size={16} className="me-2" />
                {t("planning_purchasing.tab_analytics", "Analytics")}
              </NavLink>
            </NavItem>
          )}
        </Nav>
      )}
      {canViewPO && moduleTab === "purchase_orders" ? (
        <PurchaseOrders locations={locations} enumData={enumData} orgCurrency={orgCurrency} defaultTaxRate={orgDefaultTaxRate}
          initialPrefill={poPrefill} onPrefillConsumed={() => setPoPrefill(null)} />
      ) : canTransfer && moduleTab === "transfers" ? (
        <TransfersTab />
      ) : canViewAnalytics && moduleTab === "analytics" ? (
        <ProcurementAnalytics />
      ) : (
      <>
      {/* Header */}
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="d-flex align-items-center gap-3 w-100"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Spec §5: admins don't create requests — they initiate a Transfer here or a
              PO from the Purchase Orders tab's own button. Requesters keep New Request. */}
          {canResolve ? (
            canTransfer && (
              <button className="btn btn-success" onClick={() => setAdminTransferModal(true)}>
                <FeatherIcon icon="repeat" size={18} className="me-1" />
                {t("planning_purchasing.new_transfer", "New Transfer")}
              </button>
            )
          ) : (
            <PermissionGuard permission={PROCUREMENT_REQUEST_CREATE}>
              <button className="btn btn-success" onClick={() => toggleCreateModal()}>
                <FeatherIcon icon="plus" size={18} className="me-1" />
                {t("planning_purchasing.new_request", "New Request")}
              </button>
            </PermissionGuard>
          )}
          <div className="ms-auto" style={{ minWidth: "300px", flexGrow: 1, maxWidth: "500px" }}>
            <Input
              type="text"
              placeholder={t("planning_purchasing.search_requests", "Search requests...")}
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </motion.div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.length > 0 && canResolve && (
        <div className="d-flex align-items-center gap-3 mb-3 p-2 px-3 rounded bg-light border">
          <span className="fw-medium">
            {selectedIds.length} {t("planning_purchasing.selected", "selected")}
          </span>
          <button className="btn btn-danger btn-sm" onClick={() => setBulkRejectModal(true)}>
            <FeatherIcon icon="x-circle" size={14} className="me-1" />
            {t("planning_purchasing.reject_selected", "Reject Selected")}
          </button>
          <button className="btn btn-link btn-sm text-muted" onClick={() => setSelectedIds([])}>
            {t("shared.clear", "Clear")}
          </button>
        </div>
      )}

      <Card>
        <Nav pills className="justify-content-start px-3 pt-3 gap-2 flex-wrap">
          {STATUS_TABS.map((tab) => (
            <NavItem key={tab}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <NavLink
                  style={{ cursor: "pointer" }}
                  className={`tab-btn ${statusFilter === tab ? "active" : ""}`}
                  onClick={() => toggleTab(tab)}
                >
                  <FeatherIcon icon={statusMeta[tab].icon} className="me-1" size={16} />
                  {statusLabel(tab)}
                </NavLink>
              </motion.div>
            </NavItem>
          ))}
        </Nav>
        <CardHeader>
          <h3 className="card-title text-success mb-0">
            {canResolve
              ? t("planning_purchasing.title", "Planning & Purchasing")
              : t("planning_purchasing.my_requests", "My Requests")}
          </h3>
        </CardHeader>
        <CardBody>
          <div className="table-responsive" style={{ overflowX: "auto", maxHeight: "calc(100vh - 320px)", overflowY: "auto" }}>
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  {showSelectColumn && (
                    <th style={{ width: "36px" }}>
                      <input
                        type="checkbox"
                        checked={requests.length > 0 && selectedIds.length === requests.length}
                        onChange={toggleSelectAll}
                      />
                    </th>
                  )}
                  <th>#</th>
                  <th style={{ whiteSpace: "nowrap" }}>{t("planning_purchasing.request_number", "Request #")}</th>
                  <th>{t("shared.title", "Title")}</th>
                  <th>{t("planning_purchasing.request_type", "Type")}</th>
                  <th>{t("shared.status", "Status")}</th>
                  <th>{t("shared.priority", "Priority")}</th>
                  <th>{t("planning_purchasing.quantity", "Qty")}</th>
                  <th>{t("shared.location", "Location")}</th>
                  {canResolve && <th>{t("planning_purchasing.requested_by", "Requested By")}</th>}
                  <th>{t("shared.created", "Created")}</th>
                  {isPendingTab && <th>{t("shared.actions", "Actions")}</th>}
                </tr>
              </thead>
              <tbody>
                <AnimatePresence>
                  {requests?.map((request, ind) => (
                    <motion.tr
                      key={request.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      {showSelectColumn && (
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(request.id)}
                            onChange={() => toggleSelect(request.id)}
                          />
                        </td>
                      )}
                      <th scope="row">{ind + 1}</th>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span
                          className="text-primary hover-underline"
                          onClick={() => toggleDetailsModal(request)}
                          style={{ cursor: "pointer", fontWeight: 500 }}
                        >
                          {request.requestNumber || "-"}
                        </span>
                      </td>
                      <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={request.title}>
                        {request.title}
                      </td>
                      <td>
                        {request.requestType === "ASSET"
                          ? t("planning_purchasing.type_asset", "Asset")
                          : t("planning_purchasing.type_parts_consumables", "Parts & Consumables")}
                      </td>
                      <td>{getStatusBadge(request.status)}</td>
                      <td>
                        {request.urgencyLevel ? (
                          <span className={`badge rounded-pill py-1 px-2 text-uppercase ${urgencyColors[request.urgencyLevel] || "bg-secondary"}`}>
                            {t(`shared.${request.urgencyLevel.toLowerCase()}`, request.urgencyLevel)}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {request.quantity}
                        {request.unit ? ` ${request.unit}` : ""}
                      </td>
                      <td>{request.locationName || <span className="text-muted">-</span>}</td>
                      {canResolve && <td>{request.requester?.name || "-"}</td>}
                      <td>{formatCreatedDate(request.createdAt)}</td>
                      {isPendingTab && (
                        <td>
                          <div className="d-flex gap-2">
                            {canResolve && (
                              <span
                                className="text-danger"
                                role="button"
                                title={t("planning_purchasing.reject_button", "Reject")}
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setRejectModal(true);
                                }}
                              >
                                <FeatherIcon icon="x-circle" size={18} />
                              </span>
                            )}
                            {!canResolve && isOwner(request) && (
                              <>
                                <PermissionGuard permission={PROCUREMENT_REQUEST_CREATE}>
                                  <span className="text-warning" role="button" onClick={() => toggleCreateModal(request)}>
                                    <FeatherIcon icon="edit" size={18} />
                                  </span>
                                </PermissionGuard>
                                <PermissionGuard permission={PROCUREMENT_REQUEST_CREATE}>
                                  <span
                                    className="text-danger"
                                    role="button"
                                    onClick={() => {
                                      setSelectedRequest(request);
                                      setDeleteModal(true);
                                    }}
                                  >
                                    <FeatherIcon icon="trash-2" size={18} />
                                  </span>
                                </PermissionGuard>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="text-center py-3">
              <Spinner color="primary" />
            </div>
          )}

          {!loading && requests.length === 0 && (
            <div className="text-center py-5">
              <FeatherIcon icon="inbox" size={48} className="text-muted mb-3" />
              <p className="text-muted">{t("planning_purchasing.no_requests", "No requests found")}</p>
            </div>
          )}

          <div className="text-center mt-3">
            {hasMore && requests.length > 0 ? (
              <Button color="primary" onClick={loadMore} disabled={loading}>
                {loading ? t("shared.loading", "Loading...") : t("shared.load_more", "Load More")}
              </Button>
            ) : (
              requests.length > 0 && (
                <div className="text-muted">{t("planning_purchasing.no_more_requests", "No more requests to load")}</div>
              )
            )}
          </div>
        </CardBody>
      </Card>

      {/* Create / Edit modal */}
      <ProcurementRequestModal
        isOpen={createModal}
        toggle={() => toggleCreateModal()}
        requestData={selectedRequest}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        errors={errors}
        apiError={apiError}
        locations={locations}
        enumData={enumData}
        orgCurrency={orgCurrency}
      />

      {/* Details modal */}
      <Modal isOpen={detailsModal} toggle={() => toggleDetailsModal()} size="lg">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="modal-content border-0 shadow-lg"
          style={{ borderRadius: "12px", overflow: "hidden" }}
        >
          <div
            className="modal-header border-0 text-white d-flex justify-content-between align-items-center"
            style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", padding: "1.5rem" }}
          >
            <div className="d-flex align-items-center">
              <div className="me-3">
                <div className="bg-white bg-opacity-25 rounded-circle d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                  <FeatherIcon icon="shopping-cart" size={24} className="text-white" />
                </div>
              </div>
              <div>
                <h4 className="modal-title mb-1 text-white">
                  {selectedRequest?.requestNumber || t("planning_purchasing.request_details", "Request Details")}
                </h4>
                <div className="d-flex align-items-center gap-3">
                  <span className="text-white small">
                    <FeatherIcon icon="calendar" size={14} className="me-1" />
                    {formatCreatedDate(selectedRequest?.createdAt)}
                  </span>
                  <span className="text-white small">
                    <FeatherIcon icon="user" size={14} className="me-1" />
                    {selectedRequest?.requester?.name || "-"}
                  </span>
                </div>
              </div>
            </div>
            <button type="button" className="btn-close btn-close-white" onClick={() => toggleDetailsModal()} aria-label="Close" />
          </div>

          <div className="modal-body p-4" style={{ maxHeight: "60vh", overflow: "auto" }}>
            {selectedRequest && (
              <Row className="g-4">
                <Col md={6}>
                  <div className="bg-light rounded p-3 h-100">
                    <h6 className="text-primary mb-3 fw-semibold">
                      <FeatherIcon icon="info" size={16} className="me-2" />
                      {t("shared.basic_information", "Basic Information")}
                    </h6>
                    <div className="mb-3">
                      <label className="text-muted small">{t("shared.title", "Title")}</label>
                      <div className="fw-medium">{selectedRequest.title}</div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">{t("planning_purchasing.request_type", "Type")}</label>
                      <div className="fw-medium">
                        {selectedRequest.requestType === "ASSET"
                          ? t("planning_purchasing.type_asset", "Asset")
                          : t("planning_purchasing.type_parts_consumables", "Parts & Consumables")}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">{t("shared.status", "Status")}</label>
                      <div>{getStatusBadge(selectedRequest.status)}</div>
                    </div>
                    {selectedRequest.assetId && (
                      <div className="mb-3">
                        <label className="text-muted small">{t("planning_purchasing.related_asset", "Related Asset")}</label>
                        <div className="fw-medium">
                          <FeatherIcon icon="box" size={14} className="me-1 text-muted" />
                          {selectedRequest.assetName || `#${selectedRequest.assetId}`}
                        </div>
                      </div>
                    )}
                    <div className="mb-3">
                      <label className="text-muted small">{t("planning_purchasing.quantity", "Quantity")}</label>
                      <div className="fw-medium">
                        {selectedRequest.quantity}
                        {selectedRequest.unit ? ` ${selectedRequest.unit}` : ""}
                      </div>
                    </div>
                    <div>
                      <label className="text-muted small">{t("shared.priority", "Priority")}</label>
                      <div>
                        {selectedRequest.urgencyLevel ? (
                          <span className={`badge rounded-pill py-1 px-2 text-uppercase ${urgencyColors[selectedRequest.urgencyLevel] || "bg-secondary"}`}>
                            {t(`shared.${selectedRequest.urgencyLevel.toLowerCase()}`, selectedRequest.urgencyLevel)}
                          </span>
                        ) : "-"}
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="bg-light rounded p-3 h-100">
                    <h6 className="text-primary mb-3 fw-semibold">
                      <FeatherIcon icon="map-pin" size={16} className="me-2" />
                      {t("planning_purchasing.location_timing", "Location & Timing")}
                    </h6>
                    <div className="mb-3">
                      <label className="text-muted small">{t("shared.location", "Location")}</label>
                      <div className="fw-medium">{selectedRequest.locationName || "-"}</div>
                    </div>
                    <div className="mb-3">
                      <label className="text-muted small">{t("planning_purchasing.usage_type", "Usage Type")}</label>
                      <div className="fw-medium">
                        {selectedRequest.usageType === "TEMPORARY"
                          ? t("planning_purchasing.usage_temporary", "Temporary")
                          : t("planning_purchasing.usage_permanent", "Permanent")}
                      </div>
                    </div>
                    {selectedRequest.usageType === "TEMPORARY" ? (
                      <>
                        <div className="mb-3">
                          <label className="text-muted small">{t("planning_purchasing.start_date", "Start Date")}</label>
                          <div className="fw-medium">{formatDate(selectedRequest.startDate)}</div>
                        </div>
                        <div>
                          <label className="text-muted small">{t("planning_purchasing.end_date", "End Date")}</label>
                          <div className="fw-medium">{formatDate(selectedRequest.endDate)}</div>
                        </div>
                      </>
                    ) : (
                      <div>
                        <label className="text-muted small">{t("planning_purchasing.required_by_date", "Required By")}</label>
                        <div className="fw-medium">{formatDate(selectedRequest.requiredByDate)}</div>
                      </div>
                    )}
                  </div>
                </Col>

                {/* New item / vendor details */}
                {selectedRequest.newItemFlag && (
                  <Col md={12}>
                    <div className="bg-light rounded p-3">
                      <h6 className="text-primary mb-3 fw-semibold">
                        <FeatherIcon icon="package" size={16} className="me-2" />
                        {t("planning_purchasing.new_item_details", "New Item / Vendor Details")}
                      </h6>
                      <Row>
                        <Col md={4}><label className="text-muted small">{t("planning_purchasing.manufacturer", "Manufacturer")}</label><div className="fw-medium">{selectedRequest.manufacturer || "-"}</div></Col>
                        <Col md={4}><label className="text-muted small">{t("planning_purchasing.model_reference", "Model / Reference")}</label><div className="fw-medium">{selectedRequest.modelReference || "-"}</div></Col>
                        <Col md={4}><label className="text-muted small">{t("planning_purchasing.suggested_vendor", "Suggested Vendor")}</label><div className="fw-medium">
                          {selectedRequest.suggestedVendor || "-"}
                          {selectedRequest.suggestedVendorId && (
                            <span className="badge bg-success-subtle text-success ms-2">{t("planning_purchasing.directory_vendor", "Directory vendor")}</span>
                          )}
                        </div></Col>
                      </Row>
                      {selectedRequest.estimatedPrice != null && (
                        <div className="mt-2">
                          <label className="text-muted small">{t("planning_purchasing.estimated_price", "Estimated Unit Price")}</label>
                          <div className="fw-medium">
                            {selectedRequest.estimatedPrice} {selectedRequest.currency || ""}{" "}
                            <span className="text-muted small">
                              ({selectedRequest.estimatedPriceIncludesTax ? t("planning_purchasing.incl_tax", "incl. tax") : t("planning_purchasing.excl_tax", "excl. tax")})
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </Col>
                )}

                {/* Notes */}
                {selectedRequest.notes && (
                  <Col md={12}>
                    <div className="bg-light rounded p-3">
                      <h6 className="text-primary mb-2 fw-semibold">
                        <FeatherIcon icon="file-text" size={16} className="me-2" />
                        {t("planning_purchasing.notes", "Additional Notes")}
                      </h6>
                      <p className="mb-0">{selectedRequest.notes}</p>
                    </div>
                  </Col>
                )}

                {/* Attachments */}
                {selectedRequest.attachments?.length > 0 && (
                  <Col md={12}>
                    <div className="bg-light rounded p-3">
                      <h6 className="text-primary mb-3 fw-semibold">
                        <FeatherIcon icon="paperclip" size={16} className="me-2" />
                        {t("planning_purchasing.attachments", "Attachments")}
                      </h6>
                      <div className="d-flex gap-2 flex-wrap">
                        {selectedRequest.attachments.map((url, idx) => (
                          <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                            <FeatherIcon icon="file" size={14} className="me-1" />
                            {t("shared.document", "Document")} {idx + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  </Col>
                )}

                {/* Resolution details (transfer assets / linked PO) — requester-visible */}
                {(selectedRequest.transferredAssets?.length > 0 || selectedRequest.linkedPoNumber) && (
                  <Col md={12}>
                    <div className="rounded p-3" style={{ backgroundColor: "#d1e7dd" }}>
                      <h6 className="text-success mb-2 fw-semibold">
                        <FeatherIcon icon="check-circle" size={16} className="me-2" />
                        {t("planning_purchasing.resolution", "Resolution")}
                      </h6>
                      {(selectedRequest.transferredAssets || []).map((a, idx) => (
                        <div key={idx} className="d-flex align-items-center flex-wrap gap-2 mb-1">
                          <FeatherIcon icon="box" size={14} className="text-muted" />
                          <span className="fw-medium">{a.assetName}</span>
                          <span className={`badge ${a.status === "TEMPORARY_TRANSFERRED" ? "bg-warning-subtle text-warning-emphasis" : "bg-primary-subtle text-primary"}`}>
                            {a.status === "TEMPORARY_TRANSFERRED"
                              ? t("business.planning.temporary_transfer", "Temporary Transfer")
                              : t("business.planning.permanent_transfer", "Permanent Transfer")}
                          </span>
                          {a.scheduled && (
                            <span className="badge bg-info-subtle text-info">
                              {t("planning_purchasing.arrives_on", "Arrives on {{date}}", { date: formatDate(a.startDate) })}
                            </span>
                          )}
                          {a.endDate && (
                            <span className="badge bg-warning-subtle text-warning-emphasis">
                              {t("planning_purchasing.on_loan_until", "On loan until {{date}}", { date: formatDate(a.endDate) })}
                            </span>
                          )}
                        </div>
                      ))}
                      {selectedRequest.linkedPoNumber && (
                        <div className="d-flex align-items-center gap-2">
                          <FeatherIcon icon="file-text" size={14} className="text-muted" />
                          <span className="fw-medium">{selectedRequest.linkedPoNumber}</span>
                          {selectedRequest.linkedPoStatus && (
                            <span className="badge bg-light text-dark border text-uppercase">
                              {t(`planning_purchasing.postatus.${selectedRequest.linkedPoStatus.toLowerCase()}`,
                                selectedRequest.linkedPoStatus.charAt(0) + selectedRequest.linkedPoStatus.slice(1).toLowerCase().replace(/_/g, " "))}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </Col>
                )}

                {/* Rejection reason */}
                {selectedRequest.status === "REJECTED" && (
                  <Col md={12}>
                    <div className="rounded p-3" style={{ backgroundColor: "#f8d7da" }}>
                      <h6 className="text-danger mb-2 fw-semibold">
                        <FeatherIcon icon="x-circle" size={16} className="me-2" />
                        {t("planning_purchasing.rejection_reason", "Rejection Reason")}
                      </h6>
                      <p className="mb-0">{selectedRequest.rejectionReason || t("planning_purchasing.no_reason", "No reason provided")}</p>
                    </div>
                  </Col>
                )}

                {/* Admin notes (internal) */}
                {canResolve && (
                  <Col md={12}>
                    <div className="rounded p-3" style={{ backgroundColor: "#fff3cd" }}>
                      <h6 className="text-warning-emphasis mb-2 fw-semibold">
                        <FeatherIcon icon="lock" size={16} className="me-2" />
                        {t("planning_purchasing.admin_notes", "Internal Admin Notes")}
                        <span className="text-muted small ms-2">{t("planning_purchasing.not_visible_requester", "(not visible to the requester)")}</span>
                      </h6>
                      <textarea
                        className="form-control mb-2"
                        rows={2}
                        value={adminNotesDraft}
                        onChange={(e) => setAdminNotesDraft(e.target.value)}
                        placeholder={t("planning_purchasing.admin_notes_placeholder", "Add internal notes...")}
                      />
                      <button className="btn btn-sm btn-outline-secondary" onClick={handleSaveAdminNotes} disabled={savingNotes}>
                        {savingNotes ? <Spinner size="sm" /> : t("planning_purchasing.save_notes", "Save Notes")}
                      </button>
                    </div>
                  </Col>
                )}
              </Row>
            )}
          </div>

          <div className="modal-footer border-top bg-light">
            {canResolve && selectedRequest?.status === "SUBMITTED" && (
              <>
                {/* Mirrors the Service Requests footer: one primary Approve action, one Reject, Close. */}
                <UncontrolledDropdown direction="up">
                  <DropdownToggle color="success" caret className="d-flex align-items-center gap-1">
                    <FeatherIcon icon="check" size={14} />
                    {t("shared.approve", "Approve")}
                  </DropdownToggle>
                  <DropdownMenu>
                    <DropdownItem onClick={() => { setDetailsModal(false); setTransferError(""); setTransferModal(true); }}>
                      <FeatherIcon icon="repeat" size={14} className="me-2" />
                      {t("planning_purchasing.approve_via_transfer", "Via internal transfer")}
                    </DropdownItem>
                    {canViewPO && (
                      <DropdownItem onClick={() => issuePoFromRequest(selectedRequest)}>
                        <FeatherIcon icon="file-plus" size={14} className="me-2" />
                        {t("planning_purchasing.approve_via_po", "Via purchase order")}
                      </DropdownItem>
                    )}
                  </DropdownMenu>
                </UncontrolledDropdown>
                <button
                  className="btn btn-danger d-flex align-items-center gap-1"
                  onClick={() => {
                    setDetailsModal(false);
                    setRejectModal(true);
                  }}
                >
                  <FeatherIcon icon="x" size={14} />
                  {t("planning_purchasing.reject_button", "Reject")}
                </button>
              </>
            )}
            <button type="button" className="btn btn-secondary" onClick={() => toggleDetailsModal()}>
              {t("shared.close", "Close")}
            </button>
          </div>
        </motion.div>
      </Modal>

      {/* Reject (single) */}
      <RejectProcurementRequestModal
        isOpen={rejectModal}
        toggle={() => setRejectModal(false)}
        request={selectedRequest}
        onReject={handleReject}
        isSubmitting={isSubmitting}
      />

      {/* Reject (bulk) */}
      <RejectProcurementRequestModal
        isOpen={bulkRejectModal}
        toggle={() => setBulkRejectModal(false)}
        bulkCount={selectedIds.length}
        onReject={handleBulkReject}
        isSubmitting={isSubmitting}
      />

      {/* Admin-initiated transfer — the live asset-transfer flow, no request involved */}
      <AssetTransferModal
        isOpen={adminTransferModal}
        toggle={() => setAdminTransferModal(false)}
        bulk
        organizationId={organizationId}
        onSuccess={() => {
          setAdminTransferModal(false);
          // The transfer lives on the Transfers tab, not among procurement requests — show the
          // user what they just created instead of leaving them on a list it will never appear in.
          setModuleTab("transfers");
        }}
      />

      {/* Resolve via internal transfer */}
      <TransferResolutionModal
        isOpen={transferModal}
        toggle={() => setTransferModal(false)}
        request={selectedRequest}
        onResolve={handleResolveTransfer}
        isSubmitting={isSubmitting}
        apiError={transferError}
      />

      {/* Delete confirmation */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <div className="modal-header">
          <h5 className="modal-title">{t("shared.confirm_delete", "Confirm Delete")}</h5>
          <button type="button" className="btn-close" onClick={() => setDeleteModal(false)} aria-label="Close" />
        </div>
        <div className="modal-body">
          <p>{t("planning_purchasing.confirm_delete", "Are you sure you want to delete this request?")}</p>
          <p className="text-muted small mb-0">
            <strong>{t("shared.title", "Title")}:</strong> {selectedRequest?.title}
          </p>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setDeleteModal(false)}>
            {t("shared.cancel", "Cancel")}
          </button>
          <button type="button" className="btn btn-danger" onClick={handleDelete}>
            {t("shared.delete", "Delete")}
          </button>
        </div>
      </Modal>
      </>
      )}
      {/* Same tab treatment as Settings/Reporting (their card look comes from these
          page-local rules, not the theme): white cards, blue text + underline when active. */}
      <style>{`
        .planning-tabs {
          gap: 0.5rem;
          flex-wrap: nowrap;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .planning-tabs::-webkit-scrollbar {
          display: none;
        }
        .planning-tabs .nav-link {
          color: #6c757d;
          background-color: #fff;
          white-space: nowrap;
        }
        .planning-tabs .nav-link.active {
          color: #3980c0;
          background-color: #fff;
          border-bottom: 2px solid #3980c0;
        }
      `}</style>
    </div>
  );
};

export default PlanningPurchasing;
